import { useState } from "react";
import { Card } from "@volca/ui/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { ICM_TAB, IcmTab } from "@/icm/constants";
import type { EnrichedIcmStatement, IcmJob } from "@/icm/types";
import { IcmJobBreakdownTable } from "./job-breakdown-table";
import { IcmTechnicianBreakdownTable } from "./technician-breakdown-table";

interface IcmPageBreakdownCardProps {
  periodStart?: string;
  statements: EnrichedIcmStatement[];
  jobs: IcmJob[];
  onSelectJob: (job: IcmJob | null) => void;
}

export const IcmPageBreakdownCard = ({
  periodStart,
  statements,
  jobs,
  onSelectJob,
}: IcmPageBreakdownCardProps) => {
  const [activeTab, setActiveTab] = useState<IcmTab>(ICM_TAB.technicians);
  const [tabSearch, setTabSearch] = useState("");

  return (
    <Card className="w-full gap-0 overflow-hidden py-0">
      <Tabs
        value={activeTab}
        onValueChange={(tab) => {
          setActiveTab(tab as IcmTab);
          setTabSearch("");
        }}
        className="relative flex flex-1 flex-col gap-0 overflow-hidden"
      >
        <Stack
          gap={4}
          direction="row"
          align="center"
          justify="between"
          className="w-full grow-0 border-b"
        >
          <TabsList variant="line" className="justify-start">
            <TabsTrigger value={ICM_TAB.technicians}>
              Technician breakdown
            </TabsTrigger>
            <TabsTrigger value={ICM_TAB.jobs}>Job breakdown</TabsTrigger>
          </TabsList>
          <SearchInput
            placeholder="Search..."
            value={tabSearch}
            onChange={setTabSearch}
            className="mr-4"
          />
        </Stack>
        <TabsContent value={ICM_TAB.technicians}>
          <IcmTechnicianBreakdownTable
            periodStart={periodStart}
            statements={statements}
            searchValue={tabSearch}
            setSearchValue={setTabSearch}
          />
        </TabsContent>
        <TabsContent value={ICM_TAB.jobs}>
          {jobs.length > 0 ? (
            <IcmJobBreakdownTable
              jobs={jobs}
              searchValue={tabSearch}
              setSearchValue={setTabSearch}
              onRowClick={onSelectJob}
            />
          ) : (
            <Empty className={emptyStateVariants({ layout: "inline" })}>
              <EmptyHeader>
                <EmptyDescription>
                  No job data available for this period.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </TabsContent>
      </Tabs>
    </Card>
  );
};
